import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

// Cashfree JS SDK is provider-specific by nature (this is the checkout return
// page). Loaded on demand; mode comes from the return_url (?m=), never hardcoded.
function loadCashfreeSdk() {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve(window.Cashfree);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = () => (window.Cashfree ? resolve(window.Cashfree) : reject(new Error("SDK unavailable")));
    s.onerror = () => reject(new Error("Could not load the payment SDK"));
    document.body.appendChild(s);
  });
}

// Same colour contract as PublicMenu.jsx (menu_*_color) — reused, not reinvented.
// TabOn values are fallbacks ONLY, used when a venue has set no colours.
const FALLBACK = {
  menu_primary_color: "#9D00FF",
  menu_secondary_color: "#2D1B4E",
  menu_background_color: "#0A0A0A",
  menu_card_color: "#1A1A1A",
  menu_text_color: "#FFFFFF",
  menu_accent_text_color: "#A0A0A0",
  menu_logo_url: null,
};

/**
 * Post-payment landing page (Cashfree return_url = /order/:orderId). This page
 * belongs to the VENUE — it renders in the venue's identity; TabOn recedes.
 * Polls the public status endpoint — the browser NEVER marks an order paid; only
 * the webhook or the verified poller may. Shows Confirming → Paid / Not completed
 * / still-confirming.
 */
export default function OrderStatus() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("m") === "production" ? "production" : "sandbox";

  const [phase, setPhase] = useState("confirming"); // confirming | paid | collected | failed | cancelled | timeout
  const [order, setOrder] = useState(null);          // full status payload
  const [venue, setVenue] = useState(null);          // {name, menu_slug, branding}
  const [retrying, setRetrying] = useState(false);
  const stopped = useRef(false);

  const poll = useCallback(async () => {
    stopped.current = false;
    // ~4 minutes total with backoff: 2s for the first 30s (the webhook case — fast
    // feedback), then 5s. Reconciliation now settles a missed webhook within ~90s +
    // a cycle, so a poller-settled payment resolves on screen instead of the page
    // giving up first. NOT indefinite — a phone left open overnight must not hammer
    // the endpoint.
    const start = Date.now();
    const TOTAL_MS = 4 * 60 * 1000;
    while (!stopped.current && Date.now() - start < TOTAL_MS) {
      try {
        const { data } = await api.get(`/public/orders/${orderId}/status`);
        setOrder(data);
        if (data.venue) setVenue(data.venue);
        if (data.payment_status === "paid") {
          // Paid AND already handed over → terminal; don't promise a future WhatsApp (M-3a). "collected"
          // is the only terminal order state here besides "cancelled" (handled below); pending/preparing/
          // ready — and room "delivered", which settles elsewhere — are still in progress. We branch on
          // the status the FIRST poll returns; we do NOT extend the poll's lifetime to follow an order (3b).
          setPhase(data.status === "collected" ? "collected" : "paid");
          return;
        }
        // Cancelled (the expiry swept it, or staff cancelled it) is terminal and takes precedence
        // over "failed" — a cancelled order is not retryable, it is re-orderable (I-3). Stop polling.
        if (data.status === "cancelled") { setPhase("cancelled"); return; }
        // Show the confirming spinner ONLY while a payment is genuinely in flight — a "created"
        // attempt whose webhook we are still awaiting. Every other non-terminal state resolves to
        // "payment not completed, try again": payment_status unpaid/failed, OR "pending" with a last
        // attempt that is failed/dropped OR UNKNOWN/null. "We don't know" is not "in progress" (K-5) —
        // showing the spinner on an unknown state stranded pre-denormalisation orders (e.g. #169) on
        // "We're confirming your payment…" with no way out. A fresh retry is a new "created" attempt.
        if (data.payment_status === "unpaid" || data.payment_status === "failed" ||
            (data.payment_status === "pending" && data.last_attempt_status !== "created")) {
          setPhase("failed"); return;
        }
        // "pending" + "created" → a payment is genuinely in flight → keep waiting for the webhook.
      } catch (_) { /* transient — keep trying */ }
      const delay = (Date.now() - start) < 30000 ? 2000 : 5000;
      await new Promise((r) => setTimeout(r, delay));
    }
    if (!stopped.current) setPhase("timeout");
  }, [orderId]);

  useEffect(() => {
    poll();
    return () => { stopped.current = true; };
  }, [poll]);

  const retry = async () => {
    setRetrying(true);
    try {
      const { data: sess } = await api.post(`/public/orders/${orderId}/payment-session`, {});
      const Cashfree = await loadCashfreeSdk();
      const cf = Cashfree({ mode });
      cf.checkout({ paymentSessionId: sess.payment_session_id, redirectTarget: "_self" });
      // The page now navigates to the payment page; it returns here afterwards.
    } catch (e) {
      // 409 = already paid — reflect that instead of showing an error.
      if (e?.response?.status === 409) {
        setRetrying(false);
        setPhase("paid");
        return;
      }
      setRetrying(false);
      setPhase("failed");
    }
  };

  // "Order again" (I-3): rebuild the cart from the order's lines and return to the menu, turning a
  // lost sale into a recovered one. We write PublicMenu's own localStorage cart (same key + shape +
  // cartLineKey format) then hard-navigate; PublicMenu restores and re-prices it on mount. A room
  // order re-orders under ?context=room so it lands on the right cart, like the original.
  const reorder = () => {
    const sl = venue?.menu_slug;
    if (!sl) return;
    const roomCtx = order?.order_type === "room";
    try {
      const lines = (order?.items || []).map((it) => {
        const ids = (it.addons || []).map((a) => a && a.id).filter(Boolean);
        return {
          key: `${it.item_id}|${it.variant_id || ""}|${[...ids].sort().join(",")}`,
          item_id: it.item_id, name: it.name,
          variant_id: it.variant_id || null, variant_label: it.variant_label || null,
          addon_option_ids: ids, addons: it.addons || [],
          qty: Math.max(1, parseInt(it.qty, 10) || 1),
          unit_price: it.unit_price, note: it.note || "",
        };
      });
      if (lines.length) {
        window.localStorage.setItem(
          `pm-cart:${sl}:${roomCtx ? "room" : "table"}`,
          JSON.stringify({ savedAt: Date.now(), lines }),
        );
      }
    } catch (_) { /* storage blocked — the menu still opens, just without the pre-filled cart */ }
    window.location.assign(roomCtx ? `/menu/${sl}?context=room` : `/menu/${sl}`);
  };

  // ── Palette derived from the venue payload (fallbacks only when unset) ──
  const b = { ...FALLBACK, ...(venue?.branding || {}) };
  const primary = b.menu_primary_color;     // the one accent: tick, order number, button
  const bg = b.menu_background_color;
  const surface = b.menu_card_color;
  const heading = b.menu_text_color;
  const muted = b.menu_accent_text_color;
  const logo = b.menu_logo_url;
  const hairline = `${heading}1F`;           // ~12% of the text colour — works light or dark
  const venueName = venue?.name || null;
  const slug = venue?.menu_slug || null;

  // ── Supporting detail (paid) ──
  const num = order?.order_number != null ? `#${order.order_number}` : "";
  // Render the backend's already-rounded total as received — no second, independent rounding here
  // (M-3c). The public status payload has sent a rounded `total` since Prompt K; re-rounding it in a
  // second place is exactly how 724.50 survived a full prompt cycle.
  const amount = order?.total != null ? `₹${order.total}` : "";
  // The follow-up build ships payment_instrument.detail (masked, e.g. "Visa ••••
  // 2123"); fall back to a generic label. When the order was settled at the counter (staff took
  // cash/UPI after the online attempt failed), say so — the retry route is closed (H-4c).
  const paidOnline = order?.payment_method === "online";
  const paidBy = order?.payment_instrument?.detail
    ? `Paid by ${order.payment_instrument.detail}`
    : (paidOnline ? "Paid online" : "Paid at the counter");
  let paidTime = "";
  if (order?.paid_at) {
    try { paidTime = new Date(order.paid_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
    catch (_) { paidTime = ""; }
  }
  const detailRow = [amount, paidBy, paidTime].filter(Boolean).join(" · ");

  const H1 = { fontSize: 19, fontWeight: 700, color: heading, margin: "0 0 6px", lineHeight: 1.3 };
  const P = { fontSize: 14, color: muted, margin: 0, maxWidth: 320, lineHeight: 1.5 };

  // The order number, shown in confirming/timeout too (the paid phase has its own).
  // A fixed-height container reserves the space so nothing shifts when the number
  // arrives from the first poll; before then it holds a quiet placeholder.
  const orderNumberBlock = (
    <div style={{ minHeight: 46, display: "flex", alignItems: "center", justifyContent: "center", margin: "6px 0 14px" }}>
      {num
        ? <span data-testid="os-order-number" style={{ fontSize: "2.6rem", lineHeight: 1.02, fontWeight: 800, color: primary }}>{num}</span>
        : <span style={{ fontSize: 13, fontWeight: 600, color: muted }}>Order&nbsp;…</span>}
    </div>
  );

  return (
    <div style={{
      minHeight: "100dvh", background: bg, color: heading, display: "flex",
      alignItems: "center", justifyContent: "center", padding: "1.25rem",
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", boxSizing: "border-box",
    }}>
      <style>{`
        .os-btn:focus-visible, .os-link:focus-visible {
          outline: 2px solid ${primary}; outline-offset: 3px; border-radius: 8px;
        }
        @media (prefers-reduced-motion: no-preference) { .os-spin { animation: os-rot 1.6s linear infinite; } }
        @keyframes os-rot { to { transform: rotate(360deg); } }
      `}</style>

      <div
        data-testid="order-status-card"
        style={{
          width: "100%", maxWidth: 400, minHeight: 340, boxSizing: "border-box",
          background: surface, border: `1px solid ${hairline}`, borderRadius: 20,
          padding: "2rem 1.5rem 1.75rem", textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        }}
      >
        {/* Venue identity — the customer recognises it; confirms they're in the right place */}
        {logo
          ? <img src={logo} alt={venueName || "Venue"} style={{ height: 44, width: "auto", maxWidth: 170, objectFit: "contain", marginBottom: 8 }} />
          : <div style={{ height: 8 }} />}
        {venueName && <div style={{ fontSize: 13, fontWeight: 600, color: muted, marginBottom: 22 }}>{venueName}</div>}

        {phase === "confirming" && (
          <>
            <Clock size={30} className="os-spin" aria-hidden style={{ color: muted, marginBottom: 14 }} />
            <h1 style={H1}>We're confirming your payment</h1>
            {orderNumberBlock}
            <p style={P}>
              This usually takes a few seconds. We'll send you a WhatsApp confirmation as soon as it's
              done — you don't need to keep this page open, and <b style={{ color: heading }}>please don't pay again.</b>
            </p>
          </>
        )}

        {(phase === "paid" || phase === "collected") && (
          <>
            <CheckCircle2 size={34} aria-hidden style={{ color: primary, marginBottom: 10 }} />
            <h1 style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.01em", color: muted, margin: "0 0 2px" }}>
              Payment successful
            </h1>
            {num && (
              <div data-testid="os-order-number" style={{
                fontSize: "2.9rem", lineHeight: 1.02, fontWeight: 800, color: primary, margin: "2px 0 12px",
              }}>
                {num}
              </div>
            )}
            {detailRow && <div style={{ fontSize: 13.5, color: muted, marginBottom: 16 }}>{detailRow}</div>}
            <p style={{ ...P, marginBottom: 20 }}>
              {phase === "collected"
                ? "This order has been collected — enjoy!"
                : "We'll message you on WhatsApp as soon as it's ready to collect."}
            </p>
            {slug && (
              <a className="os-link" href={`/menu/${slug}`}
                 style={{ fontSize: 13, fontWeight: 600, color: primary, textDecoration: "none" }}>
                Back to menu
              </a>
            )}
          </>
        )}

        {phase === "failed" && (
          <>
            <AlertCircle size={32} aria-hidden style={{ color: muted, marginBottom: 12 }} />
            <h1 style={H1}>Payment not completed</h1>
            <p style={{ ...P, marginBottom: 22 }}>Your payment didn't go through. You can try again.</p>
            <button
              className="os-btn" onClick={retry} disabled={retrying}
              style={{
                width: "100%", maxWidth: 280, padding: "0.85rem", borderRadius: 12, fontWeight: 700,
                fontSize: 15, background: primary, color: "#fff", border: "none",
                cursor: retrying ? "default" : "pointer", opacity: retrying ? 0.65 : 1,
              }}
            >
              {retrying ? "Starting…" : "Try payment again"}
            </button>
          </>
        )}

        {phase === "cancelled" && (
          <>
            <AlertCircle size={32} aria-hidden style={{ color: muted, marginBottom: 12 }} />
            <h1 style={H1}>Order {num || ""} was cancelled</h1>
            <p style={{ ...P, marginBottom: 10 }}>
              {order?.payment_status === "paid" || order?.payment_status === "refund_initiated" || order?.payment_status === "refunded"
                ? "This order was cancelled and a refund is on its way."
                : <>This order was cancelled because the payment wasn't completed.{" "}
                    <b style={{ color: heading }}>You have not been charged.</b></>}
            </p>
            {(order?.items || []).length > 0 && slug && (
              <button
                className="os-btn" onClick={reorder}
                style={{
                  width: "100%", maxWidth: 280, padding: "0.85rem", borderRadius: 12, fontWeight: 700,
                  fontSize: 15, background: primary, color: "#fff", border: "none", cursor: "pointer",
                  marginTop: 10,
                }}
              >
                Order again
              </button>
            )}
          </>
        )}

        {phase === "timeout" && (
          <>
            <Clock size={30} aria-hidden style={{ color: muted, marginBottom: 12 }} />
            <h1 style={H1}>We're still checking</h1>
            {orderNumberBlock}
            <p style={P}>
              This is taking longer than usual. We'll send you a WhatsApp confirmation if it goes
              through — <b style={{ color: heading }}>please don't pay again</b>, and if you're at the
              venue, speak to the counter.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
